<script>
import { apiSecond } from '@/boot/axios'
import './admin.css'
import {
	categoryBadgeStyle,
	DEFAULT_CATEGORY_COLOR,
	LIMITS,
	apiErrorMessage,
} from '@/components/system-updates/utils'

const HEX_RE = /^#[0-9a-f]{6}$/i

function emptyForm() {
	return { id: null, name_ru: '', name_kz: '', color: '', sort: 0 }
}

// Админка: категории обновлений (вкладки на публичной странице)
export default {
	name: 'SystemUpdateCategories',
	data() {
		return {
			headers: [
				{ text: this.$t('headers.action'), value: 'action', sortable: false, width: '100px' },
				{ text: this.$t('system_updates.admin.field_name_ru'), value: 'name_ru' },
				{ text: this.$t('system_updates.admin.field_name_kz'), value: 'name_kz' },
				{ text: this.$t('system_updates.admin.field_color'), value: 'color', width: '170px' },
				{ text: this.$t('system_updates.admin.field_sort'), value: 'sort', width: '110px' },
				{
					text: this.$t('system_updates.admin.col_updates'),
					value: 'updates_count',
					width: '120px',
				},
			],
			list: [],
			loading: false,
			error: null,

			dialog: false,
			form: emptyForm(),
			errors: {},
			formError: null,
			saving: false,
			deletingId: null,
			defaultColor: DEFAULT_CATEGORY_COLOR,
			nameMax: LIMITS.categoryName,
		}
	},
	computed: {
		canManage() {
			return this.$store.getters.userPermission(['manage_system_update_categories'])
		},
		colorValid() {
			return !this.form.color || HEX_RE.test(this.form.color)
		},
		pickerColor() {
			return this.colorValid && this.form.color ? this.form.color : DEFAULT_CATEGORY_COLOR
		},
	},
	watch: {
		dialog(open) {
			if (open) this.$modal.show('su_category_modal')
			else this.$modal.hide('su_category_modal')
		},
	},
	mounted() {
		this.loadList()
	},
	methods: {
		categoryBadgeStyle,

		async loadList() {
			if (!this.canManage) return
			this.loading = true
			this.error = null
			try {
				const { data } = await apiSecond.get('v1/admin/system-update-categories')
				this.list = Array.isArray(data && data.data) ? data.data : []
			} catch (error) {
				this.list = []
				this.error = apiErrorMessage(error, this.$t('system_updates.load_error'))
			} finally {
				this.loading = false
			}
		},

		openCreate() {
			const maxSort = this.list.reduce((max, item) => Math.max(max, Number(item.sort) || 0), 0)
			this.form = { ...emptyForm(), sort: this.list.length ? maxSort + 1 : 0 }
			this.errors = {}
			this.formError = null
			this.dialog = true
		},

		openEdit(item) {
			this.form = {
				id: item.id,
				name_ru: item.name_ru || '',
				name_kz: item.name_kz || '',
				color: item.color || '',
				sort: Number(item.sort) || 0,
			}
			this.errors = {}
			this.formError = null
			this.dialog = true
		},

		closeDialog() {
			if (this.saving) return
			this.dialog = false
		},

		onPick(event) {
			this.form.color = String(event.target.value || '').toUpperCase()
		},

		validate() {
			const errors = {}
			const required = this.$t('system_updates.admin.error_required')
			;['name_ru', 'name_kz'].forEach((field) => {
				const value = (this.form[field] || '').trim()
				if (!value) errors[field] = required
				else if (value.length > LIMITS.categoryName)
					errors[field] = this.$t('system_updates.admin.error_max', { max: LIMITS.categoryName })
			})
			if (!this.colorValid) errors.color = this.$t('system_updates.admin.error_color')
			if (!Number.isInteger(Number(this.form.sort))) {
				errors.sort = this.$t('system_updates.admin.error_integer')
			}
			this.errors = errors
			return !Object.keys(errors).length
		},

		async save() {
			if (this.saving || !this.validate()) return
			this.saving = true
			this.formError = null

			const payload = {
				name_ru: this.form.name_ru.trim(),
				name_kz: this.form.name_kz.trim(),
				color: this.form.color ? this.form.color.toUpperCase() : null,
				sort: Number(this.form.sort) || 0,
			}

			try {
				if (this.form.id) {
					await apiSecond.put('v1/admin/system-update-categories/' + this.form.id, payload)
				} else {
					await apiSecond.post('v1/admin/system-update-categories', payload)
				}
				this.dialog = false
				this.loadList()
			} catch (error) {
				const data = (error && error.response && error.response.data) || {}
				const fieldErrors = data.errors || data.error_messages
				if (error && error.response && error.response.status === 422 && fieldErrors) {
					const errors = {}
					Object.keys(fieldErrors).forEach((key) => {
						const messages = fieldErrors[key]
						errors[key] = Array.isArray(messages) ? messages[0] : String(messages)
					})
					this.errors = errors
				}
				this.formError = apiErrorMessage(error, this.$t('system_updates.admin.save_error'))
			} finally {
				this.saving = false
			}
		},

		async remove(item) {
			if (this.deletingId) return
			// Занятую категорию бэк всё равно не удалит — объясняем сразу, без запроса
			if (item.updates_count > 0) {
				this.$dialog.alert({
					title: this.$t('system_updates.admin.category_in_use'),
					text: this.$t('system_updates.admin.category_in_use_text', {
						count: item.updates_count,
					}),
				})
				return
			}
			const confirmed = await this.$dialog.confirm({
				title: this.$t('system_updates.admin.category_delete_confirm', { name: item.name_ru }),
				text: this.$t('system_updates.admin.category_delete_text'),
				confirmText: this.$t('button.delete'),
			})
			if (!confirmed) return

			this.deletingId = item.id
			try {
				await apiSecond.delete('v1/admin/system-update-categories/' + item.id)
				this.loadList()
			} catch (error) {
				if (error && error.response && error.response.status === 404) {
					this.loadList()
				} else {
					// 422 — категорию успели занять обновлением
					this.$dialog.alert({
						title: apiErrorMessage(error, this.$t('system_updates.admin.delete_error')),
					})
					this.loadList()
				}
			} finally {
				this.deletingId = null
			}
		},
	},
}
</script>

<template>
	<div class="wrap__content" :class="{ 'wrap__content--preloader': !$store.state.userObject }">
		<v-preloader v-if="!$store.state.userObject"></v-preloader>

		<div v-else class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['SystemUpdateCategories']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<div class="card__content-header">
								<div
									class="content__title w-full flex flex-wrap justify-between items-center gap-3"
								>
									<div class="content__title--element">
										<div class="content__title--text">
											{{ $t('sidebar.system_update_categories') }}
										</div>
									</div>
									<div v-if="canManage" class="ml-auto">
										<a class="btn--link btn--link--plus pointer" @click="openCreate">
											{{ $t('system_updates.admin.add_category') }}
											<img src="@/assets/img/icon-plus.svg" />
										</a>
									</div>
								</div>
							</div>

							<div class="card__content--body">
								<div v-if="!canManage" class="su-admin-denied">
									{{ $t('system_updates.admin.forbidden') }}
								</div>

								<div v-else class="table__block--content">
									<div v-if="error" class="su-admin-error">
										<span>{{ error }}</span>
										<button type="button" class="su-admin-link" @click="loadList">
											{{ $t('system_updates.retry') }}
										</button>
									</div>

									<v-data-table
										:headers="headers"
										:items="list"
										:loading="loading"
										:loading-text="$t('button.loading')"
										:no-data-text="$t('system_updates.admin.categories_empty')"
										disable-sort
										hide-default-footer
										:items-per-page="-1"
									>
										<template slot="item.action" slot-scope="{ item }">
											<div class="flex">
												<a
													class="tb__table--btn tb__table--edit"
													v-tooltip.top-center="{
														content: $t('tooltip.edit'),
														class: ['tooltip__btn'],
													}"
													@click="openEdit(item)"
												>
													<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M9 15h6.75M12.375 2.625a1.591 1.591 0 112.25 2.25L5.25 14.25l-3 .75.75-3 9.375-9.375z" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
												</a>
												<a
													class="tb__table--btn tb__table--delete"
													:class="{
														'su-admin-busy': deletingId === item.id,
														'su-admin-muted': item.updates_count > 0,
													}"
													v-tooltip.top-center="{
														content:
															item.updates_count > 0
																? $t('system_updates.admin.category_in_use')
																: $t('tooltip.remove'),
														class: ['tooltip__btn'],
													}"
													@click="remove(item)"
												>
													<svg version="1.0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" v-bind:svg-inline="''" v-bind:style="'width: 18px'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="red"><path d="M213.9 1.8c-7.8 2.1-15.1 6.6-21.2 13.1-9.6 10.3-11.7 17.7-11.7 41.8V75H60l.2 30.2.3 30.3h391l.3-30.3.2-30.2H331.1l-.3-20.3c-.3-18.3-.6-20.7-2.7-26-4.3-10.9-15-21.4-26.1-25.9C296.7.6 295.2.5 258.5.3c-33.5-.2-38.8 0-44.6 1.5zM293 32.2c7.2 3.8 8 6.4 8 26V75h-90V58.2c0-19.5.8-22.1 8-25.9 4.2-2.3 4.9-2.3 36.9-2.3 31.9 0 32.8.1 37.1 2.2zM93 166.3c0 .2 6.1 69.8 13.5 154.7 8.7 100.3 14 156.3 15 159.6 4.3 14.3 16.7 25.9 32.2 29.9 8.6 2.2 196 2.2 204.6 0 15.5-4 27.9-15.6 32.2-29.9 1-3.3 6.3-59.3 15-159.6 7.4-84.9 13.5-154.5 13.5-154.7 0-.2-73.3-.3-163-.3-89.6 0-163 .1-163 .3zm117.8 157.4l-.3 82.8-14.7.3-14.8.3V241h30l-.2 82.7zm60.2.3v83h-30V241h30v83zm60 0v83.1l-14.7-.3-14.8-.3-.3-82.8-.2-82.7h30v83z"/></g></svg>
												</a>
											</div>
										</template>
										<template slot="item.name_ru" slot-scope="{ item }">
											<span class="su-admin-badge" :style="categoryBadgeStyle(item)">
												{{ item.name_ru }}
											</span>
										</template>
										<template slot="item.color" slot-scope="{ item }">
											<span class="su-color">
												<span
													class="su-color__swatch"
													:style="{ backgroundColor: item.color || defaultColor }"
												></span>
												{{ item.color || $t('system_updates.admin.color_default') }}
											</span>
										</template>
										<template slot="item.updates_count" slot-scope="{ item }">
											{{ item.updates_count || 0 }}
										</template>
									</v-data-table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<modal
			name="su_category_modal"
			class="su-modal"
			:width="520"
			:max-width="520"
			:adaptive="true"
			height="auto"
			:scrollable="true"
			:click-to-close="!saving"
			@closed="dialog = false"
		>
			<div class="su-form-dialog">
				<div class="su-form-dialog__head">
					<div class="su-form-dialog__title">
						{{
							form.id
								? $t('system_updates.admin.edit_category')
								: $t('system_updates.admin.add_category')
						}}
					</div>
					<button
						type="button"
						class="su-form-dialog__close"
						:disabled="saving"
						@click="closeDialog"
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2.2"
							stroke-linecap="round"
						>
							<path d="M6 6l12 12M18 6 6 18" />
						</svg>
					</button>
				</div>

				<form class="su-form-dialog__body" novalidate @submit.prevent="save">
					<div v-if="formError" class="su-admin-error">{{ formError }}</div>

					<label class="su-field">
						<span class="su-field__label">{{ $t('system_updates.admin.field_name_ru') }} *</span>
						<input
							v-model="form.name_ru"
							class="su-field__input"
							:class="{ 'has-error': errors.name_ru }"
							:maxlength="nameMax"
							type="text"
						/>
						<span v-if="errors.name_ru" class="su-field__error">{{ errors.name_ru }}</span>
					</label>

					<label class="su-field">
						<span class="su-field__label">{{ $t('system_updates.admin.field_name_kz') }} *</span>
						<input
							v-model="form.name_kz"
							class="su-field__input"
							:class="{ 'has-error': errors.name_kz }"
							:maxlength="nameMax"
							type="text"
						/>
						<span v-if="errors.name_kz" class="su-field__error">{{ errors.name_kz }}</span>
					</label>

					<div class="su-field">
						<span class="su-field__label">{{ $t('system_updates.admin.field_color') }}</span>
						<div class="su-color-input">
							<input
								class="su-color-input__picker"
								type="color"
								:value="pickerColor"
								:aria-label="$t('system_updates.admin.field_color')"
								@input="onPick"
							/>
							<input
								v-model.trim="form.color"
								class="su-field__input"
								:class="{ 'has-error': errors.color }"
								maxlength="7"
								placeholder="#2BB673"
								type="text"
							/>
							<button
								v-if="form.color"
								type="button"
								class="su-admin-link"
								@click="form.color = ''"
							>
								{{ $t('system_updates.admin.color_reset') }}
							</button>
						</div>
						<span v-if="errors.color" class="su-field__error">{{ errors.color }}</span>
						<span v-else class="su-field__hint">
							{{ $t('system_updates.admin.color_hint') }}
							<span class="su-admin-badge" :style="categoryBadgeStyle({ color: form.color })">
								{{ form.name_ru || $t('system_updates.admin.field_name_ru') }}
							</span>
						</span>
					</div>

					<label class="su-field">
						<span class="su-field__label">{{ $t('system_updates.admin.field_sort') }}</span>
						<input
							v-model.number="form.sort"
							class="su-field__input su-field__input--short"
							:class="{ 'has-error': errors.sort }"
							type="number"
							step="1"
						/>
						<span v-if="errors.sort" class="su-field__error">{{ errors.sort }}</span>
						<span v-else class="su-field__hint">{{ $t('system_updates.admin.sort_hint') }}</span>
					</label>

					<div class="su-form-dialog__actions">
						<button
							type="button"
							class="su-admin-btn su-admin-btn--ghost"
							:disabled="saving"
							@click="closeDialog"
						>
							{{ $t('button.cancel') }}
						</button>
						<button type="submit" class="su-admin-btn su-admin-btn--primary" :disabled="saving">
							<v-progress-circular
								v-if="saving"
								:width="2"
								:size="18"
								indeterminate
								color="white"
							/>
							<span v-else>{{ $t('button.save') }}</span>
						</button>
					</div>
				</form>
			</div>
		</modal>
	</div>
</template>
